import React, { useEffect, useState } from 'react';
import { Button, Card, Popconfirm, Row, Skeleton, Table, Modal, notification, Form } from 'antd';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { DeleteOutlined } from '@ant-design/icons';
import ResourceForm from './ResourceForm';
import { useIsAuthenticated } from '@azure/msal-react';

export const GET_ALL_RESOURCES = gql`
  query Resources($input: String) {
    resources(input: $input) {
      data {
        id
        type
        isInternal
        firstName
        lastName
        phoneNumber
        email
        isChiefChc
        company
        discipline
        employeeId
      }
    }
  }
`;

export const DELETE_RESOURCE = gql`
  mutation DeleteResource($input: ResourceQuery) {
    deleteResource(input: $input) {
      result
    }
  }
`;

const Resources: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { proceedingId } = useParams<any>();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [resourceId, setResourceId] = useState('');
  const [deleteResource, { loading: deleting, error: deleteError, data: deleteData }] =
    useMutation(DELETE_RESOURCE);
  const [getAllResources, { data: allResources, loading, error, refetch }] =
    useLazyQuery(GET_ALL_RESOURCES);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (tab === '5' && proceedingId) {
      getAllResources({
        variables: {
          input: proceedingId,
        },
      });
    }
  }, [tab]);

  useEffect(() => {
    if (deleteData?.deleteResource?.result === 'Success') {
      refetch();
      notification.success({
        message: 'Success',
        description: 'Resource has been successfully deleted.',
      });
    } else if (deleteError) {
      notification.error({
        message: 'Error',
        description: `Error in deleting resource: ${deleteError.message}`,
      });
    }
  }, [deleteData, deleteError, refetch]);

  const delResource = async (value: string) => {
    try {
      await deleteResource({
        variables: {
          input: {
            proceedingNumber: proceedingId,
            resourceId: value,
          },
        },
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: `Error in deleting resource: ${error.message}`,
      });
    }
  };

  const handleEdit = (resource: any) => {
    setResourceId(resource ? resource.id : '');
    if (!resource) {
      form.resetFields([
        'firstName',
        'lastName',
        'phoneNumber',
        'email',
        'company',
        'discipline',
        'isInternal',
        'isChiefCHC',
        'search',
        'type',
      ]);
      setResourceId('');
    }
    setIsModalVisible(true);
  };

  const closeEditModal = (resource: any) => {
    setIsModalVisible(false);
    refetch();
  };

  const resources = allResources?.resources?.data.map((resource: any) => ({
    ...resource,
    key: resource.id,
  }));

  const columns: any = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (_: any, record: any) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Phone #',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
  ];
  if (isAuthenticated) {
    columns.push({
      title: 'Delete',
      key: 'deleteIcon',
      width: 50,
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      render: (_: any, resource: any) => (
        <Popconfirm
          title="Delete Resource"
          description={'Are you sure to delete this Resource?'}
          onConfirm={() => delResource(resource.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            icon={<DeleteOutlined />}
            size={'small'}
            style={{ backgroundColor: 'white', color: 'red' }}
          />
        </Popconfirm>
      ),
    });
  }
  if (loading || deleting) return <Skeleton />;
  if (error || deleteError) return <p>Error: {error?.message || deleteError?.message}</p>;

  return (
    <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
      <Row>
        {isAuthenticated && (
          <Button type="primary" onClick={() => handleEdit(null)} style={{ marginLeft: '10px' }}>
            Add Resource
          </Button>
        )}        
      </Row>
      <Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexGrow: 10,
          }}
        >
          <Card
            title={<span className="highlighted-title">Resources for this Proceeding</span>}
            style={{ width: '100%', marginTop: '25px' }}
            className="card"
          >
            <Table
              dataSource={resources}
              loading={loading}
              columns={columns}
              onRow={(record: any) => ({
                onClick: () => handleEdit(record),
                style: { cursor: 'pointer' },
              })}
              rowClassName="clickable-row"
            />
          </Card>
        </div>
      </Row>
      <Modal
        title={resourceId ? 'Edit Resource' : 'Add Resource'}
        open={isModalVisible}
        onCancel={() => closeEditModal(resourceId)}
        footer={null}
        destroyOnClose
      >
        <ResourceForm
          onCancel={() => closeEditModal(resourceId)}
          proceedingId={proceedingId}
          resourceId={resourceId}
          onSuccess={refetch}
          form={form}
        />
      </Modal>
    </div>
  );
};

export default Resources;
